/**
 * GridConfig — grid sizing, layout constants, and presets.
 */

import type { GridMask } from "./GridMask";

export interface GridConfig {
  tileSize: number;
  tileGap: number;
  margin: number;
}

export interface GridLayout {
  totalW: number;
  totalH: number;
  originX: number;
  originY: number;
  baseRow: number;
  baseCol: number;
}

export const DEFAULT_CONFIG: GridConfig = {
  tileSize: 40,
  tileGap: 5,
  margin: 12,
};

/** Auto-scale tile size to fit within pixel budget. */
export function autoTileSize(cols: number, rows: number, gap: number = 5): number {
  const maxW = 380;
  const maxH = 220;
  const fromW = Math.floor((maxW + gap) / cols - gap);
  const fromH = Math.floor((maxH + gap) / rows - gap);
  return Math.max(24, Math.min(40, fromW, fromH));
}

/** Compute pixel dimensions of a mask-based grid. */
export function maskGridSize(mask: GridMask, tileSize: number, tileGap: number): { w: number; h: number } {
  return {
    w: mask.cols * (tileSize + tileGap) - tileGap,
    h: mask.rows * (tileSize + tileGap) - tileGap,
  };
}

/** Compute layout positions from a mask and anchor point. */
export function computeMaskLayout(
  mask: GridMask,
  tileSize: number,
  tileGap: number,
  centerX: number,
  startY: number,
): GridLayout {
  const b = mask.bounds();
  const cols = b.maxCol - b.minCol + 1;
  const rows = b.maxRow - b.minRow + 1;
  const totalW = cols * (tileSize + tileGap) - tileGap;
  const totalH = rows * (tileSize + tileGap) - tileGap;
  return {
    totalW,
    totalH,
    originX: centerX - totalW / 2,
    originY: startY,
    baseRow: b.minRow,
    baseCol: b.minCol,
  };
}
